import React, { useState, useMemo } from 'react';
import { Card, Button, Row, Col, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faCheck,
  faTrash,
  faEdit,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import SoftBadge from 'components/common/SoftBadge';
import {
  useReminders,
  useCompleteReminder,
  useDeleteReminder
} from 'hooks/useReminders';
import { useAuthContext } from 'context/authContext';
import CreateReminderModal from './CreateReminderModal';

const REMINDER_TYPE_COLORS = {
  follow_up: 'info',
  payment: 'success',
  meeting: 'primary',
  task: 'secondary',
  update: 'warning'
};

const REMINDER_TYPE_LABELS = {
  follow_up: 'Follow Up',
  payment: 'Payment',
  meeting: 'Meeting',
  task: 'Task',
  update: 'Update'
};

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const RemindersCalendar = () => {
  const { user } = useAuthContext();
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);

  // Get start and end of the current month view (including overflow days)
  const startOfMonth = currentDate.startOf('month');
  const endOfMonth = currentDate.endOf('month');
  const startOfCalendar = startOfMonth.startOf('week');
  const endOfCalendar = endOfMonth.endOf('week');

  // Fetch reminders for the visible month range
  const { reminders, isLoading } = useReminders({
    startDate: startOfCalendar.format('YYYY-MM-DD'),
    endDate: endOfCalendar.format('YYYY-MM-DD'),
    includeCompleted: true
  });

  const { completeReminder, isCompleting } = useCompleteReminder();
  const { deleteReminder, isDeleting } = useDeleteReminder();

  // Group reminders by date
  const remindersByDate = useMemo(() => {
    const grouped = {};
    reminders.forEach(reminder => {
      const dateKey = dayjs(reminder.due_date).format('YYYY-MM-DD');
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(reminder);
    });
    return grouped;
  }, [reminders]);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const days = [];
    let day = startOfCalendar;
    while (day.isBefore(endOfCalendar) || day.isSame(endOfCalendar, 'day')) {
      days.push(day);
      day = day.add(1, 'day');
    }
    return days;
  }, [startOfCalendar, endOfCalendar]);

  // Get reminders for selected date
  const selectedDateReminders = useMemo(() => {
    if (!selectedDate) return [];
    const dateKey = selectedDate.format('YYYY-MM-DD');
    return remindersByDate[dateKey] || [];
  }, [selectedDate, remindersByDate]);

  const handlePrevMonth = () => {
    setCurrentDate(currentDate.subtract(1, 'month'));
    setSelectedDate(null);
  };

  const handleNextMonth = () => {
    setCurrentDate(currentDate.add(1, 'month'));
    setSelectedDate(null);
  };

  const handleToday = () => {
    setCurrentDate(dayjs());
    setSelectedDate(dayjs());
  };

  const handleDateClick = day => {
    setSelectedDate(day);
  };

  const handleComplete = id => {
    completeReminder(id);
  };

  const handleDelete = id => {
    if (window.confirm('Delete this reminder?')) {
      deleteReminder(id);
    }
  };

  const handleEdit = reminder => {
    setEditingReminder(reminder);
    setShowReminderModal(true);
  };

  const handleAddReminder = () => {
    setEditingReminder(null);
    setShowReminderModal(true);
  };

  const handleCloseModal = () => {
    setShowReminderModal(false);
    setEditingReminder(null);
  };

  const isToday = day => day.isSame(dayjs(), 'day');
  const isCurrentMonth = day => day.month() === currentDate.month();
  const isSelected = day => selectedDate && day.isSame(selectedDate, 'day');

  return (
    <Row className="g-3 mb-3">
        <Col lg={8}>
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <Button
                  variant="falcon-default"
                  size="sm"
                  onClick={handlePrevMonth}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </Button>
                <Button
                  variant="falcon-default"
                  size="sm"
                  onClick={handleNextMonth}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
                <h5 className="mb-0 ms-2">{currentDate.format('MMMM YYYY')}</h5>
              </div>
              <div className="d-flex gap-2">
                <Button variant="falcon-default" size="sm" onClick={handleToday}>
                  Today
                </Button>
                <Button variant="primary" size="sm" onClick={handleAddReminder}>
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Add Reminder
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  {/* Days of week header */}
                  <div
                    className="d-grid border-bottom"
                    style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}
                  >
                    {DAYS_OF_WEEK.map(day => (
                      <div
                        key={day}
                        className="text-center py-2 fw-bold text-600 fs--1"
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar grid */}
                  <div
                    className="d-grid"
                    style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}
                  >
                    {calendarDays.map(day => {
                      const dateKey = day.format('YYYY-MM-DD');
                      const dayReminders = remindersByDate[dateKey] || [];
                      const hasReminders = dayReminders.length > 0;
                      const pendingReminders = dayReminders.filter(
                        r => r.status !== 'completed'
                      );
                      const hasOverdue = pendingReminders.some(r =>
                        dayjs(r.due_date).isBefore(dayjs(), 'day')
                      );

                      return (
                        <div
                          key={dateKey}
                          onClick={() => handleDateClick(day)}
                          className={`
                            p-2 border cursor-pointer position-relative
                            ${!isCurrentMonth(day) ? 'bg-100 text-400' : ''}
                            ${isSelected(day) ? 'bg-primary-subtle' : ''}
                            ${isToday(day) ? 'border-primary border-2' : ''}
                          `}
                          style={{
                            minHeight: '90px',
                            cursor: 'pointer'
                          }}
                        >
                          <div
                            className={`
                              fw-semi-bold mb-1
                              ${isToday(day) ? 'text-primary' : ''}
                            `}
                          >
                            {day.format('D')}
                          </div>
                          {hasReminders && (
                            <div className="d-flex flex-wrap gap-1">
                              {dayReminders.slice(0, 3).map(reminder => {
                                const isCompleted =
                                  reminder.status === 'completed';
                                return (
                                  <Badge
                                    key={reminder.id}
                                    bg={
                                      isCompleted
                                        ? 'light'
                                        : hasOverdue &&
                                            dayjs(reminder.due_date).isBefore(
                                              dayjs(),
                                              'day'
                                            )
                                          ? 'danger'
                                          : REMINDER_TYPE_COLORS[
                                              reminder.reminder_type
                                            ]
                                    }
                                    className={`text-truncate fs--2 ${
                                      isCompleted
                                        ? 'text-decoration-line-through text-600'
                                        : ''
                                    }`}
                                    style={{ maxWidth: '100%' }}
                                    title={
                                      isCompleted
                                        ? `${reminder.title} (Completed)`
                                        : reminder.title
                                    }
                                  >
                                    {reminder.title.substring(0, 10)}
                                    {reminder.title.length > 10 ? '...' : ''}
                                  </Badge>
                                );
                              })}
                              {dayReminders.length > 3 && (
                                <Badge bg="secondary" className="fs--2">
                                  +{dayReminders.length - 3}
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="sticky-top" style={{ top: '1rem' }}>
            <Card.Header>
              <h6 className="mb-0">
                {selectedDate
                  ? selectedDate.format('dddd, MMMM D, YYYY')
                  : 'Select a date'}
              </h6>
            </Card.Header>
            <Card.Body
              className="p-0"
              style={{ maxHeight: '500px', overflowY: 'auto' }}
            >
              {!selectedDate ? (
                <div className="text-center py-4 text-muted">
                  Click on a date to see reminders
                </div>
              ) : selectedDateReminders.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <p className="mb-2">No reminders for this date</p>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddReminder}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Add Reminder
                  </Button>
                </div>
              ) : (
                selectedDateReminders.map(reminder => {
                  const isCompleted = reminder.status === 'completed';
                  const isOverdue =
                    !isCompleted &&
                    dayjs(reminder.due_date).isBefore(dayjs(), 'day');
                  const isCreatedBySelf =
                    reminder.created_by_user_id === user?.id;

                  return (
                    <div
                      key={reminder.id}
                      className={`p-3 border-bottom hover-bg-100 ${
                        isCompleted ? 'bg-100' : ''
                      }`}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-1 me-2">
                          <div
                            className={`fw-semi-bold mb-1 fs-0 ${
                              isCompleted
                                ? 'text-500 text-decoration-line-through'
                                : 'text-800'
                            }`}
                          >
                            {reminder.title}
                          </div>
                          {reminder.description && (
                            <p
                              className={`mb-2 fs--1 ${
                                isCompleted ? 'text-400' : 'text-600'
                              }`}
                            >
                              {reminder.description}
                            </p>
                          )}
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <SoftBadge
                              bg={REMINDER_TYPE_COLORS[reminder.reminder_type]}
                              pill
                            >
                              {REMINDER_TYPE_LABELS[reminder.reminder_type]}
                            </SoftBadge>
                            {isCompleted && (
                              <Badge bg="success" pill>
                                Completed
                              </Badge>
                            )}
                            {isOverdue && (
                              <Badge bg="danger" pill>
                                Overdue
                              </Badge>
                            )}
                            {reminder.created_by && (
                              <small className="text-500">
                                by {reminder.created_by.first_name}{' '}
                                {reminder.created_by.last_name}
                              </small>
                            )}
                            {isCompleted && reminder.assigned_to && (
                              <small className="text-500">
                                completed by{' '}
                                {reminder.assigned_to.first_name}{' '}
                                {reminder.assigned_to.last_name}
                                {reminder.completed_at &&
                                  ` on ${dayjs(reminder.completed_at).format(
                                    'DD/MM/YYYY'
                                  )}`}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="d-flex gap-1">
                          {isCreatedBySelf && !isCompleted && (
                            <>
                              <Button
                                variant="falcon-primary"
                                size="sm"
                                onClick={() => handleEdit(reminder)}
                                title="Edit"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </Button>
                              <Button
                                variant="falcon-danger"
                                size="sm"
                                onClick={() => handleDelete(reminder.id)}
                                disabled={isDeleting}
                                title="Delete"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                            </>
                          )}
                          {!isCompleted && (
                            <Button
                              variant="falcon-success"
                              size="sm"
                              onClick={() => handleComplete(reminder.id)}
                              disabled={isCompleting}
                              title="Complete"
                            >
                              <FontAwesomeIcon icon={faCheck} />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </Card.Body>
          </Card>
        </Col>
      <CreateReminderModal
        show={showReminderModal}
        onHide={handleCloseModal}
        editingReminder={editingReminder}
        defaultValues={
          selectedDate
            ? { due_date: selectedDate.toDate() }
            : {}
        }
      />
    </Row>
  );
};

export default RemindersCalendar;
